// Owner-path → colour bridge, shared by the live process views (via
// ProcessMode's useOwnerColor) and the process image export. Maps the unified
// node-coloring layer (keyed by `moduleKey`, id-first) to the process views'
// owner paths (written module paths, possibly short refs): full path first,
// then unique suffix; ambiguous suffixes resolve to nothing.
import type { ArchitectureModel, Module } from "@archlang/scene/arch-model";
import { moduleKey } from "@archlang/scene/arch-model";
import type { NodeColoring } from "../board/coloring/types.js";

export type OwnerColorFn = (path: readonly string[]) => string | undefined;

export function ownerColorForModel(
  model: ArchitectureModel,
  coloring: NodeColoring,
): OwnerColorFn {
  const byPath = new Map<string, string>();
  const bySuffix = new Map<string, string | null>(); // null = ambiguous
  (function walk(mods: readonly Module[], parent: readonly string[]) {
    for (const m of mods) {
      const path = [...parent, m.name];
      const key = moduleKey(m);
      byPath.set(path.join("."), key);
      for (let k = 1; k <= path.length; k++) {
        const suf = path.slice(path.length - k).join(".");
        bySuffix.set(suf, bySuffix.has(suf) ? null : key);
      }
      walk(m.children ?? [], path);
    }
  })(model.modules, []);
  return (path: readonly string[]) => {
    const joined = path.join(".");
    const key = byPath.get(joined) ?? bySuffix.get(joined);
    return key ? coloring.colorForNode(key) : undefined;
  };
}
